/**
 * Utilities for the Desktop Admin "Delete User Docs" feature.
 *
 * Responsibilities:
 *   - Discover every Firestore document associated with a target user
 *     (as scheduler, as werker, as associate, chat participant,
 *     announcement author/recipient, crew member, etc.).
 *   - Delete documents in batches (chunked at 500 ops per writeBatch).
 *   - Discover and delete Storage files by convention.
 *   - Write audit log entries for every deletion (including dry runs).
 *
 * All operations are client-side. Firebase Auth user deletion is
 * intentionally NOT included — the admin removes the Auth account
 * manually via the Firebase console after Firestore cleanup.
 */

import {
  DocumentReference,
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  deleteObject,
  listAll,
  ref as storageRef,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { FB_COLLECTION_PATH } from "../functions/postActionTypes";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Firestore writeBatch limit is 500 ops per commit. Stay under it. */
const BATCH_SIZE = 450;

/** Top-level collection where audit entries are written. */
const ADMIN_AUDIT_LOG = "adminAuditLog";

/** Known subcollections under `users/{uid}` to scan for deletion. */
export const USER_SUBCOLLECTIONS: readonly string[] = [
  "jobFeed",
  "jobsDocs",
  "jobUpdatePending",
  "jobFeedUpdatePending",
  "users_PublicFacing",
  "users_AssociateFacing",
  "users_BadgeCount",
  "users_NotificationsCount",
  "presence",
  "operationStatus",
  "notifications",
  "notificationpending",
  "announcementFeed",
  "deleteAccount",
  "werkerInvitePending",
  "jobEditPending",
];

/** Storage prefix conventions (best-effort discovery). */
const STORAGE_PREFIX_PROFILE_PICS = "profilePics";
const STORAGE_PREFIX_CHAT_ATTACHMENTS = "chatAttachments";
const STORAGE_PREFIX_ANNOUNCEMENT_ATTACHMENTS = "announcementAttachments";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DocEntry {
  id: string;
  path: string;
  data: Record<string, any>;
}

export interface WerkerCopy extends DocEntry {
  werkerUid: string;
  werkerName: string;
  jobStatusWerker: string;
}

export interface SchedulerJobGroup {
  uniqueJobId: string;
  jobTitle: string;
  jobDate: string;
  schedulerCopy: DocEntry | null;
  werkerCopies: WerkerCopy[];
}

export interface DanglingRef {
  kind:
    | "otherUserAssociate"
    | "announcementRecipient"
    | "crewMember";
  description: string;
  /** Docs to modify (not delete) — e.g. strip a field or array entry. */
  targetPath: string;
  /** Operation type for the cleanup action. */
  cleanup:
    | { type: "deleteField"; fieldPath: string }
    | { type: "arrayRemove"; fieldPath: string; value: string };
}

export interface UserDeletionSummary {
  uid: string;
  userRoot: DocEntry | null;
  userSettings: DocEntry | null;
  subcollections: Record<string, DocEntry[]>;
  schedulerJobs: SchedulerJobGroup[];
  werkerJobs: DocEntry[];
  associatesDoc: DocEntry | null;
  chatsDoc: DocEntry | null;
  announcementsAuthored: DocEntry[];
  crewsMember: DocEntry[];
  fcmTokens: DocEntry[];
  danglingRefs: DanglingRef[];
  storageFiles: string[];
}

export interface DeleteOptions {
  dryRun: boolean;
  adminUid: string;
  targetUid: string;
  action: string;
  /** Optional human-readable context for audit log. */
  note?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const toEntry = (d: {
  id: string;
  ref: { path: string };
  data: () => any;
}): DocEntry => ({
  id: d.id,
  path: d.ref.path,
  data: d.data() || {},
});

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
};

// ---------------------------------------------------------------------------
// Summary fetching
// ---------------------------------------------------------------------------

export const fetchUserDeletionSummary = async (
  uid: string
): Promise<UserDeletionSummary> => {
  const summary: UserDeletionSummary = {
    uid,
    userRoot: null,
    userSettings: null,
    subcollections: {},
    schedulerJobs: [],
    werkerJobs: [],
    associatesDoc: null,
    chatsDoc: null,
    announcementsAuthored: [],
    crewsMember: [],
    fcmTokens: [],
    danglingRefs: [],
    storageFiles: [],
  };

  // --- Root user + userSettings docs ---
  const [rootSnap, settingsSnap] = await Promise.all([
    getDoc(doc(db, FB_COLLECTION_PATH.Users, uid)),
    getDoc(doc(db, "userSettings", uid)),
  ]);
  if (rootSnap.exists()) {
    summary.userRoot = {
      id: rootSnap.id,
      path: rootSnap.ref.path,
      data: rootSnap.data() || {},
    };
  }
  if (settingsSnap.exists()) {
    summary.userSettings = {
      id: settingsSnap.id,
      path: settingsSnap.ref.path,
      data: settingsSnap.data() || {},
    };
  }

  // --- User subcollections (parallel fetch) ---
  const subSnaps = await Promise.all(
    USER_SUBCOLLECTIONS.map((name) =>
      getDocs(collection(db, FB_COLLECTION_PATH.Users, uid, name))
    )
  );
  USER_SUBCOLLECTIONS.forEach((name, i) => {
    const snap = subSnaps[i];
    const docs: DocEntry[] = [];
    snap.forEach((d) => docs.push(toEntry(d)));
    if (docs.length > 0) summary.subcollections[name] = docs;
  });

  // --- Jobs where user is the scheduler (top-level jobsDocs via collectionGroup) ---
  // Each scheduler-initiated job produces copies for each werker (same uniqueJobId).
  const schedulerJobsQuery = query(
    collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
    where("docStamp.userInfoFrom.uid", "==", uid)
  );
  const schedulerJobsSnap = await getDocs(schedulerJobsQuery);
  const groupsByUniqueId = new Map<string, SchedulerJobGroup>();
  schedulerJobsSnap.forEach((d) => {
    const data = d.data() || {};
    const uniqueJobId: string =
      data?.jobInfo?.core?.uniqueJobId || d.id;
    let group = groupsByUniqueId.get(uniqueJobId);
    if (!group) {
      group = {
        uniqueJobId,
        jobTitle: String(data?.jobInfo?.core?.jobTitle || ""),
        jobDate: String(data?.jobInfo?.core?.jobDate || ""),
        schedulerCopy: null,
        werkerCopies: [],
      };
      groupsByUniqueId.set(uniqueJobId, group);
    }
    const toUid = data?.docStamp?.userInfoTo?.uid;
    const entry = toEntry(d);
    if (toUid === uid) {
      group.schedulerCopy = entry;
    } else {
      group.werkerCopies.push({
        ...entry,
        werkerUid: String(toUid || ""),
        werkerName: `${data?.docStamp?.userInfoTo?.firstName || ""} ${
          data?.docStamp?.userInfoTo?.lastName || ""
        }`.trim(),
        jobStatusWerker: String(
          data?.jobInfo?.status?.jobStatusWerker || ""
        ),
      });
    }
  });
  summary.schedulerJobs = Array.from(groupsByUniqueId.values());

  // --- Jobs where user is a werker (but NOT also scheduler — excluded via path) ---
  const werkerJobsQuery = query(
    collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
    where("docStamp.userInfoTo.uid", "==", uid)
  );
  const werkerJobsSnap = await getDocs(werkerJobsQuery);
  werkerJobsSnap.forEach((d) => {
    const data = d.data() || {};
    if (data?.docStamp?.userInfoFrom?.uid === uid) {
      // This is already captured under schedulerJobs (scheduler === werker copy).
      return;
    }
    summary.werkerJobs.push(toEntry(d));
  });

  // --- Associates (user's own userAssociations doc) ---
  const assocSnap = await getDoc(
    doc(db, FB_COLLECTION_PATH.UserAssociations, uid)
  );
  if (assocSnap.exists()) {
    summary.associatesDoc = {
      id: assocSnap.id,
      path: assocSnap.ref.path,
      data: assocSnap.data() || {},
    };
  }

  // --- Chats summary doc (chats/{uid}) ---
  const chatsSnap = await getDoc(doc(db, FB_COLLECTION_PATH.Chats, uid));
  if (chatsSnap.exists()) {
    summary.chatsDoc = {
      id: chatsSnap.id,
      path: chatsSnap.ref.path,
      data: chatsSnap.data() || {},
    };
  }

  // --- Announcements authored by user ---
  try {
    const annQuery = query(
      collection(db, FB_COLLECTION_PATH.Announcements),
      where("authorUid", "==", uid)
    );
    const annSnap = await getDocs(annQuery);
    annSnap.forEach((d) => summary.announcementsAuthored.push(toEntry(d)));
  } catch (err) {
    console.warn("announcements authorUid query failed:", err);
  }

  // --- Crews where user is a member ---
  try {
    const crewQuery = query(
      collection(db, FB_COLLECTION_PATH.Crews),
      where("memberIds", "array-contains", uid)
    );
    const crewSnap = await getDocs(crewQuery);
    crewSnap.forEach((d) => summary.crewsMember.push(toEntry(d)));
  } catch (err) {
    console.warn("crews memberIds query failed:", err);
  }

  // --- FCMTokens (best-effort: any doc in FCMTokens with uid field === uid) ---
  try {
    const fcmQuery = query(
      collection(db, "FCMTokens"),
      where("uid", "==", uid)
    );
    const fcmSnap = await getDocs(fcmQuery);
    fcmSnap.forEach((d) => summary.fcmTokens.push(toEntry(d)));
  } catch (err) {
    console.warn("FCMTokens uid query failed:", err);
  }

  // --- Dangling refs ---
  // Announcement recipientUids array-contains (user is listed as recipient).
  try {
    const annRecQuery = query(
      collection(db, FB_COLLECTION_PATH.Announcements),
      where("recipientUids", "array-contains", uid)
    );
    const annRecSnap = await getDocs(annRecQuery);
    annRecSnap.forEach((d) => {
      summary.danglingRefs.push({
        kind: "announcementRecipient",
        description: `Announcement ${d.id} lists user as recipient`,
        targetPath: d.ref.path,
        cleanup: {
          type: "arrayRemove",
          fieldPath: "recipientUids",
          value: uid,
        },
      });
    });
  } catch (err) {
    console.warn("announcements recipientUids query failed:", err);
  }

  // Other users' userAssociations that contain this user as a nested key.
  // Firestore can't query "has any nested field" so we scan — this is O(N users).
  // Only attempt for small environments or when admin explicitly requests it.
  try {
    const allAssocSnap = await getDocs(
      collection(db, FB_COLLECTION_PATH.UserAssociations)
    );
    allAssocSnap.forEach((d) => {
      if (d.id === uid) return;
      const data = d.data() || {};
      if (Object.prototype.hasOwnProperty.call(data, uid)) {
        summary.danglingRefs.push({
          kind: "otherUserAssociate",
          description: `userAssociations/${d.id} has this user as associate`,
          targetPath: d.ref.path,
          cleanup: { type: "deleteField", fieldPath: uid },
        });
      }
    });
  } catch (err) {
    console.warn("userAssociations scan failed:", err);
  }

  // --- Storage files (best-effort by convention) ---
  summary.storageFiles = await discoverStorageFiles(
    uid,
    summary.chatsDoc,
    summary.announcementsAuthored
  );

  return summary;
};

// ---------------------------------------------------------------------------
// Storage discovery
// ---------------------------------------------------------------------------

const safeListAll = async (path: string): Promise<string[]> => {
  try {
    const result = await listAll(storageRef(storage, path));
    const files = result.items.map((i) => i.fullPath);
    const nested = await Promise.all(
      result.prefixes.map((p) => safeListAll(p.fullPath))
    );
    return [...files, ...nested.flat()];
  } catch (err) {
    // listAll on a non-existent prefix returns empty, so this is usually a
    // permissions or network error.
    console.warn(`Storage listAll failed for ${path}:`, err);
    return [];
  }
};

export const discoverStorageFiles = async (
  uid: string,
  chatsDoc: DocEntry | null,
  announcementsAuthored: DocEntry[]
): Promise<string[]> => {
  const paths = new Set<string>();

  // Profile pics
  for (const p of await safeListAll(`${STORAGE_PREFIX_PROFILE_PICS}/${uid}`)) {
    paths.add(p);
  }

  // Chat attachments — derive chatIds from user's chats summary doc keys.
  if (chatsDoc) {
    const chatIds = Object.keys(chatsDoc.data || {});
    const nested = await Promise.all(
      chatIds.map((cid) =>
        safeListAll(`${STORAGE_PREFIX_CHAT_ATTACHMENTS}/${cid}`)
      )
    );
    for (const p of nested.flat()) paths.add(p);
  }

  // Announcement attachments for each announcement authored by user.
  const nestedAnn = await Promise.all(
    announcementsAuthored.map((a) =>
      safeListAll(`${STORAGE_PREFIX_ANNOUNCEMENT_ATTACHMENTS}/${a.id}`)
    )
  );
  for (const p of nestedAnn.flat()) paths.add(p);

  return Array.from(paths);
};

// ---------------------------------------------------------------------------
// Deletion primitives
// ---------------------------------------------------------------------------

/**
 * Delete a set of Firestore documents by path. Chunked into 450-op batches.
 * Returns number of docs deleted (or intended if dryRun).
 */
export const deleteDocsByPath = async (
  paths: string[],
  opts: DeleteOptions
): Promise<number> => {
  if (paths.length === 0) return 0;

  if (!opts.dryRun) {
    for (const group of chunk(paths, BATCH_SIZE)) {
      const batch = writeBatch(db);
      for (const p of group) {
        batch.delete(doc(db, p));
      }
      await batch.commit();
    }
  }

  await logAdminAction({
    ...opts,
    action: `${opts.action}:delete`,
    collection: inferCollectionFromPaths(paths),
    docPaths: paths,
    affectedCount: paths.length,
  });

  return paths.length;
};

/**
 * Delete all docs returned by a query (uses collection reference + chunked batches).
 */
export const deleteDocRefs = async (
  refs: DocumentReference[],
  opts: DeleteOptions
): Promise<number> => {
  return deleteDocsByPath(
    refs.map((r) => r.path),
    opts
  );
};

/** Delete a single top-level field from a doc (e.g. associate key). */
export const deleteNestedField = async (
  docPath: string,
  fieldPath: string,
  opts: DeleteOptions
): Promise<void> => {
  if (!opts.dryRun) {
    await updateDoc(doc(db, docPath), { [fieldPath]: deleteField() });
  }
  await logAdminAction({
    ...opts,
    action: `${opts.action}:deleteField`,
    collection: docPath.split("/")[0],
    docPaths: [docPath],
    affectedCount: 1,
    note: `fieldPath=${fieldPath}`,
  });
};

/** Remove a single value from an array-typed field via arrayRemove semantics. */
export const removeFromArrayField = async (
  docPath: string,
  fieldPath: string,
  value: string,
  opts: DeleteOptions
): Promise<void> => {
  if (!opts.dryRun) {
    // Read-modify-write (keeps it simple, no FieldValue.arrayRemove import here)
    const snap = await getDoc(doc(db, docPath));
    const data: any = snap.data() || {};
    const current: any[] = Array.isArray(data[fieldPath]) ? data[fieldPath] : [];
    const next = current.filter((v) => v !== value);
    await updateDoc(doc(db, docPath), { [fieldPath]: next });
  }
  await logAdminAction({
    ...opts,
    action: `${opts.action}:arrayRemove`,
    collection: docPath.split("/")[0],
    docPaths: [docPath],
    affectedCount: 1,
    note: `${fieldPath} -= ${value}`,
  });
};

/** Delete a set of Storage files by fullPath. */
export const deleteStorageFiles = async (
  paths: string[],
  opts: DeleteOptions
): Promise<number> => {
  if (paths.length === 0) return 0;

  if (!opts.dryRun) {
    await Promise.all(
      paths.map(async (p) => {
        try {
          await deleteObject(storageRef(storage, p));
        } catch (err) {
          console.warn(`Failed to delete Storage file ${p}:`, err);
        }
      })
    );
  }

  await logAdminAction({
    ...opts,
    action: `${opts.action}:storageDelete`,
    collection: "__storage__",
    docPaths: paths,
    affectedCount: paths.length,
  });

  return paths.length;
};

/**
 * Delete an entire subcollection under users/{uid}/{name}.
 * Returns number of docs deleted.
 */
export const deleteUserSubcollection = async (
  uid: string,
  subcollection: string,
  opts: DeleteOptions
): Promise<number> => {
  const snap = await getDocs(
    collection(db, FB_COLLECTION_PATH.Users, uid, subcollection)
  );
  const paths: string[] = [];
  snap.forEach((d) => paths.push(d.ref.path));
  return deleteDocsByPath(paths, {
    ...opts,
    action: `${opts.action}:${subcollection}`,
  });
};

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

interface AuditEntry {
  adminUid: string;
  targetUid: string;
  action: string;
  collection: string;
  docPaths: string[];
  affectedCount: number;
  dryRun: boolean;
  note?: string;
}

export const logAdminAction = async (
  entry: AuditEntry & { dryRun: boolean }
): Promise<void> => {
  try {
    await addDoc(collection(db, ADMIN_AUDIT_LOG), {
      ...entry,
      timestamp: serverTimestamp(),
      clientTimestamp: Date.now(),
    });
  } catch (err) {
    console.error("Failed to write adminAuditLog entry:", err);
  }
};

const inferCollectionFromPaths = (paths: string[]): string => {
  if (paths.length === 0) return "";
  const first = paths[0];
  return first.split("/")[0] || first;
};

// ---------------------------------------------------------------------------
// Aggregate helpers (section- and user-level deletes)
// ---------------------------------------------------------------------------

/**
 * Delete every Firestore doc + Storage file for a user, in the correct
 * order: subcollections first, root last. Does NOT delete the Auth
 * account (admin does that manually via Firebase console).
 */
export const deleteEntireUser = async (
  summary: UserDeletionSummary,
  opts: DeleteOptions
): Promise<void> => {
  const uid = summary.uid;

  // 1. User subcollections
  for (const name of USER_SUBCOLLECTIONS) {
    const docs = summary.subcollections[name];
    if (docs && docs.length > 0) {
      await deleteDocsByPath(
        docs.map((d) => d.path),
        { ...opts, action: `deleteUser:subcollection:${name}` }
      );
    }
  }

  // 2. Jobs as scheduler (all copies for each uniqueJobId)
  const schedJobPaths: string[] = [];
  for (const group of summary.schedulerJobs) {
    if (group.schedulerCopy) schedJobPaths.push(group.schedulerCopy.path);
    for (const w of group.werkerCopies) schedJobPaths.push(w.path);
  }
  await deleteDocsByPath(schedJobPaths, {
    ...opts,
    action: "deleteUser:schedulerJobs",
  });

  // 3. Jobs as werker
  await deleteDocsByPath(
    summary.werkerJobs.map((d) => d.path),
    { ...opts, action: "deleteUser:werkerJobs" }
  );

  // 4. Associates doc
  if (summary.associatesDoc) {
    await deleteDocsByPath([summary.associatesDoc.path], {
      ...opts,
      action: "deleteUser:userAssociations",
    });
  }

  // 5. Chats summary doc
  if (summary.chatsDoc) {
    await deleteDocsByPath([summary.chatsDoc.path], {
      ...opts,
      action: "deleteUser:chatsSummary",
    });
  }

  // 6. Announcements authored
  await deleteDocsByPath(
    summary.announcementsAuthored.map((d) => d.path),
    { ...opts, action: "deleteUser:announcementsAuthored" }
  );

  // 7. Crews — remove uid from memberIds (don't delete the crew itself)
  for (const crew of summary.crewsMember) {
    await removeFromArrayField(crew.path, "memberIds", uid, {
      ...opts,
      action: "deleteUser:crews",
    });
  }

  // 8. FCMTokens
  await deleteDocsByPath(
    summary.fcmTokens.map((d) => d.path),
    { ...opts, action: "deleteUser:fcmTokens" }
  );

  // 9. Dangling refs
  for (const ref of summary.danglingRefs) {
    if (ref.cleanup.type === "deleteField") {
      await deleteNestedField(
        ref.targetPath,
        ref.cleanup.fieldPath,
        { ...opts, action: `deleteUser:dangling:${ref.kind}` }
      );
    } else {
      await removeFromArrayField(
        ref.targetPath,
        ref.cleanup.fieldPath,
        ref.cleanup.value,
        { ...opts, action: `deleteUser:dangling:${ref.kind}` }
      );
    }
  }

  // 10. Storage files
  await deleteStorageFiles(summary.storageFiles, {
    ...opts,
    action: "deleteUser:storage",
  });

  // 11. userSettings doc
  if (summary.userSettings) {
    await deleteDocsByPath([summary.userSettings.path], {
      ...opts,
      action: "deleteUser:userSettings",
    });
  }

  // 12. Root user doc — LAST
  if (summary.userRoot) {
    if (!opts.dryRun) {
      await deleteDoc(doc(db, summary.userRoot.path));
    }
    await logAdminAction({
      ...opts,
      action: "deleteUser:root",
      collection: FB_COLLECTION_PATH.Users,
      docPaths: [summary.userRoot.path],
      affectedCount: 1,
      dryRun: opts.dryRun,
    });
  }
};
